import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { IntakeStatusBadge } from '@/components/common/IntakeStatusBadge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { INTAKE_SUBMISSION_STATUS } from '@/constants/intake';
import { useOwnIntakeSubmissionsQuery } from '@/hooks/useIntake';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { IntakeDraftDiscardButton } from './IntakeDraftDiscardButton';

const VISIBLE_INTAKE_FORM_COUNT = 5;

export function IntakeFormsCard() {
  const { t } = useTranslation('intake');
  const navigate = useNavigate();
  const submissionsQuery = useOwnIntakeSubmissionsQuery();
  const submissions = submissionsQuery.data === undefined ? [] : submissionsQuery.data;
  const visibleSubmissions = submissions.slice(0, VISIBLE_INTAKE_FORM_COUNT);

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="ClipboardList" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('portalHome.cardTitle')}</h2>
      </div>
      <p className="text-xs text-zoomuted mb-3">{t('portalHome.hint')}</p>

      {submissionsQuery.isLoading === true ? (
        <div className="py-4 flex justify-center">
          <LoadingSpinner className="w-5 h-5" />
        </div>
      ) : visibleSubmissions.length === 0 ? (
        <p className="text-xs text-zoomuted mb-3">{t('portalHome.empty')}</p>
      ) : (
        <ul className="divide-y divide-zooborder mb-3">
          {visibleSubmissions.map(function renderSubmission(submission) {
            const petName =
              submission.petName === null || submission.petName.length === 0
                ? t('portalHome.unnamedPet')
                : submission.petName;
            return (
              <li key={submission.id} className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={function openSubmission() {
                    navigate(
                      generatePath(APPLICATION_ROUTES.clientPortalIntakeSubmission, {
                        submissionId: submission.id,
                      })
                    );
                  }}
                  className="min-w-0 flex-1 text-left py-2 min-h-[44px] flex items-center gap-2 hover:text-zooprimary"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold text-zooindigo truncate">
                      {petName}
                    </span>
                    <span className="block text-[11px] text-zoomuted">
                      {formatIsoDateTimeToTimestamp(submission.updatedAt)}
                    </span>
                  </span>
                  <IntakeStatusBadge status={submission.status} />
                </button>
                {submission.status === INTAKE_SUBMISSION_STATUS.draft ? (
                  <IntakeDraftDiscardButton submissionId={submission.id} petName={petName} />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <Button
        variant="outline"
        size="chip"
        className="w-full"
        onClick={function startNewForm() {
          navigate(APPLICATION_ROUTES.clientPortalIntake);
        }}
      >
        <Icon name="Plus" />
        {t('portalHome.startNew')}
      </Button>
    </Card>
  );
}
